import React, { useEffect, useMemo, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { Link } from "react-router-dom";
import { Card, Label, Input, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import CreateEditCompanyVehicle from "../../../../Components/popUpComponents/CreateEditCompanyVehicle";
import {
  listCompanyVehiclesApi,
  createCompanyVehicleGpsTrackerApi,
} from "../../../../helpers/fakebackend_helper";
import {
  thirdPartyGpsLiveSaveCredentials,
  thirdPartyGpsLiveSaveCredentialsSuccess,
  thirdPartyGpsLiveCheckStatus,
  thirdPartyGpsLiveTestConnection,
  thirdPartyGpsLiveTestConnectionSuccess,
  thirdPartyGpsLiveUnassign,
  thirdPartyGpsLiveUnassignSuccess,
} from "../../../../store/thirdPartyIntegration/action";
import {
  GPSLIVE_WEB_URL,
  gpsLiveDeviceUrl,
} from "../../../../helpers/gpsLiveUrl";

// GPSLive (Rewire Security) vehicle GPS tracking is per-company: each company
// pastes its own GPSLive API key, stored via savecredentials. The key powers
// the "GPS Tracking" tab on Company Vehicles (device picker + live refresh).
export default function GpsLiveIntegration() {
  const dispatch = useDispatch();

  const [editCredentials, setEditCredentials] = useState(false);
  const [apiKey, setApiKey] = useState("");
  // Change #100398: devices returned by testconnection. The first test on
  // load is silent so opening the tab lists the devices without a toast.
  const [devices, setDevices] = useState(null);
  const silentTest = useRef(false);
  const [deviceSearch, setDeviceSearch] = useState("");
  // Assign-to-vehicle popup: the device being assigned + the vehicle picked.
  const [assignDevice, setAssignDevice] = useState(null);
  const [assignVehicleId, setAssignVehicleId] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  // Change #100671: create a vehicle straight from the assign dropdown. The
  // value is the registration to prefill (from what was typed), or "" for a
  // blank form; null = closed.
  const [createVehicleReg, setCreateVehicleReg] = useState(null);

  const {
    checkStatusDataAPI,
    checkStatusLoading,
    testConnectionLoading,
    saveCredentialsDataAPI,
    testConnectionDataAPI,
    unassignDataAPI,
  } = useSelector((state) => ({
    checkStatusDataAPI:
      state.ThirdPartyIntegration.thirdPartyGpsLiveCheckStatusData,
    checkStatusLoading:
      state.ThirdPartyIntegration.thirdPartyGpsLiveCheckStatusLoading,
    saveCredentialsDataAPI:
      state.ThirdPartyIntegration.thirdPartyGpsLiveSaveCredentialsData,
    testConnectionDataAPI:
      state.ThirdPartyIntegration.thirdPartyGpsLiveTestConnectionData,
    testConnectionLoading:
      state.ThirdPartyIntegration.thirdPartyGpsLiveTestConnectionLoading,
    unassignDataAPI: state.ThirdPartyIntegration.thirdPartyGpsLiveUnassignData,
  }));

  const status = checkStatusDataAPI?.gpslive_status || {};

  useEffect(() => {
    dispatch(thirdPartyGpsLiveCheckStatus());
  }, []);

  useEffect(() => {
    if (
      status?.has_credentials === 1 &&
      devices === null &&
      !silentTest.current
    ) {
      silentTest.current = true;
      dispatch(thirdPartyGpsLiveTestConnection());
    }
  }, [status?.has_credentials]);

  useEffect(() => {
    if (isEmpty(saveCredentialsDataAPI)) return;
    if (saveCredentialsDataAPI.status === 200) {
      toast.success(saveCredentialsDataAPI?.message);
      setEditCredentials(false);
      setApiKey("");
      dispatch(thirdPartyGpsLiveSaveCredentialsSuccess([]));
      setDevices(null);
      silentTest.current = false;
      dispatch(thirdPartyGpsLiveCheckStatus());
    } else {
      toast.error(
        saveCredentialsDataAPI?.error || saveCredentialsDataAPI?.message
      );
      dispatch(thirdPartyGpsLiveSaveCredentialsSuccess([]));
    }
  }, [saveCredentialsDataAPI]);

  useEffect(() => {
    if (isEmpty(testConnectionDataAPI)) return;
    const silent = silentTest.current === true;
    silentTest.current = "done";
    if (testConnectionDataAPI.status === 200) {
      // Older backends return only device_count - leave the list unknown.
      setDevices(testConnectionDataAPI?.gpslive_device_list ?? null);
      if (!silent) {
        toast.success(
          `Connected - ${
            testConnectionDataAPI?.device_count ?? 0
          } device(s) found`
        );
      }
      dispatch(thirdPartyGpsLiveTestConnectionSuccess([]));
      dispatch(thirdPartyGpsLiveCheckStatus());
    } else {
      setDevices(null);
      if (!silent) {
        toast.error(testConnectionDataAPI?.error || "GPSLive test failed");
      }
      dispatch(thirdPartyGpsLiveTestConnectionSuccess([]));
    }
  }, [testConnectionDataAPI]);

  useEffect(() => {
    if (isEmpty(unassignDataAPI)) return;
    if (unassignDataAPI.status === 200) {
      toast.success(unassignDataAPI?.message);
      setDevices(null);
      silentTest.current = false;
      dispatch(thirdPartyGpsLiveUnassignSuccess([]));
      dispatch(thirdPartyGpsLiveCheckStatus());
    } else {
      toast.error(unassignDataAPI?.message);
      dispatch(thirdPartyGpsLiveUnassignSuccess([]));
    }
  }, [unassignDataAPI]);

  const handleSaveCredentials = () => {
    if (!apiKey.trim()) {
      toast.error("Enter your GPSLive API key");
      return;
    }
    dispatch(thirdPartyGpsLiveSaveCredentials([{ ApiKey: apiKey.trim() }]));
  };

  const handleTest = () => {
    dispatch(thirdPartyGpsLiveTestConnection());
  };

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.trim().toLowerCase();
    if (!q || !devices) return devices;
    return devices.filter((d) =>
      [
        d.Name,
        d.ProviderDeviceId,
        d.DeviceImei,
        d.LastAddress,
        d.CompanyVehicle_Title,
        d.CompanyVehicle_RegistrationPlate,
      ].some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [devices, deviceSearch]);

  const vehicleOptions = (vehicles || []).map((v) => ({
    value: String(v.Id),
    label:
      [v?.RegistrationPlate, v?.Title].filter(Boolean).join(" - ") ||
      `Vehicle #${v?.Id}`,
  }));

  const openAssign = async (device) => {
    setAssignDevice(device);
    setAssignVehicleId(
      device?.CompanyVehicle_Id ? String(device.CompanyVehicle_Id) : null
    );
    if (vehicles === null) {
      const res = await listCompanyVehiclesApi();
      setVehicles(res?.status === 200 ? res?.companyvehicle_list || [] : []);
    }
  };

  // Reload the vehicle list and pick the vehicle that was just created.
  const handleVehicleCreated = async (newVehicleId) => {
    const res = await listCompanyVehiclesApi();
    if (res?.status === 200) setVehicles(res?.companyvehicle_list || []);
    if (newVehicleId) setAssignVehicleId(String(newVehicleId));
  };

  // A device sits on one vehicle at a time: /companyvehiclegpstracker/create
  // takes it off any vehicle it is already on, so this just creates the row
  // on the new vehicle (same payload as the vehicle GPS Tracking tab).
  const handleAssignSave = async () => {
    if (!assignVehicleId) {
      toast.error("Choose a vehicle");
      return;
    }
    if (String(assignDevice?.CompanyVehicle_Id || "") === assignVehicleId) {
      setAssignDevice(null);
      return;
    }
    setAssignSaving(true);
    try {
      const res = await createCompanyVehicleGpsTrackerApi([
        {
          CompanyVehicle_Id: assignVehicleId,
          Provider: "gpslive",
          ProviderDeviceId: assignDevice?.ProviderDeviceId,
          DeviceImei: assignDevice?.DeviceImei || null,
          Name: assignDevice?.Name || null,
          FlagActive: 1,
        },
      ]);
      if (res?.status !== 200) {
        toast.error(res?.message || "Could not assign the device");
        return;
      }
      toast.success("Device assigned");
      setAssignDevice(null);
      silentTest.current = true;
      dispatch(thirdPartyGpsLiveTestConnection());
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDisconnect = () => {
    dispatch(thirdPartyGpsLiveUnassign());
  };

  if (checkStatusLoading) return <LoadingSpinner />;

  return (
    <Card className="p-3">
      <h5 className="mb-3">GPSLive Vehicle Tracking</h5>

      {editCredentials || status?.has_credentials !== 1 ? (
        <>
          <p className="text-muted">
            Enter the API key from your GPSLive (Rewire Security) account. Each
            company connects with its own GPSLive credentials. The key powers
            the GPS Tracking tab on your company vehicles.
          </p>
          <Label>API Key</Label>
          <Input
            type="password"
            value={apiKey}
            placeholder="Paste your GPSLive API key"
            onChange={(e) => setApiKey(e.target.value)}
          />
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-primary" onClick={handleSaveCredentials}>
              Save API Key
            </button>
            {status?.has_credentials === 1 && (
              <button
                className="btn btn-light"
                onClick={() => {
                  setEditCredentials(false);
                  setApiKey("");
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mb-1">
            <strong>GPSLive is connected.</strong>
          </p>
          <p className="mb-1 text-muted">
            Last test: {status?.last_test || "never"}
            {status?.last_test_count != null
              ? ` (${status.last_test_count} device(s))`
              : ""}
          </p>
          <div className="d-flex gap-2 my-2 flex-wrap">
            <button
              className="btn btn-success"
              onClick={handleTest}
              disabled={testConnectionLoading}
            >
              {testConnectionLoading ? "Testing..." : "Test Connection"}
            </button>
            <button
              className="btn btn-light"
              onClick={() => setEditCredentials(true)}
            >
              Update API Key
            </button>
            <button className="btn btn-danger" onClick={handleDisconnect}>
              Disconnect
            </button>
            <a
              className="btn btn-soft-primary"
              href={`${GPSLIVE_WEB_URL}/live-map`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open GPSLive <i className="ri-external-link-line"></i>
            </a>
          </div>

          <h6 className="mt-4 mb-2">Devices on this GPSLive account</h6>
          {devices === null ? (
            <p className="text-muted mb-0">
              {testConnectionLoading
                ? "Loading devices from GPSLive..."
                : "Run Test Connection to list your devices."}
            </p>
          ) : devices.length === 0 ? (
            <p className="text-muted mb-0">No devices found on this account.</p>
          ) : (
            <>
              <Input
                type="search"
                className="mb-2"
                style={{ maxWidth: 320 }}
                placeholder="Search devices or vehicles..."
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
              />
              <div className="table-responsive">
                <Table className="table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Device ID</th>
                      <th>IMEI</th>
                      <th>Last position</th>
                      <th>Last seen</th>
                      <th>Assigned vehicle</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevices.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-muted text-center">
                          No devices match your search.
                        </td>
                      </tr>
                    )}
                    {filteredDevices.map((d, i) => (
                      <tr key={d.ProviderDeviceId || i}>
                        <td>{d.Name || "-"}</td>
                        <td>{d.ProviderDeviceId || "-"}</td>
                        <td>{d.DeviceImei || "-"}</td>
                        <td>
                          {d.LastAddress ||
                            (d.LastLatitude != null && d.LastLongitude != null
                              ? `${d.LastLatitude}, ${d.LastLongitude}`
                              : "-")}
                        </td>
                        <td>{d.LastPositionDateTime || "-"}</td>
                        <td>
                          {d.CompanyVehicle_Id ? (
                            <Link
                              to={`/companyvehicles/${d.CompanyVehicle_Id}/view`}
                            >
                              {[
                                d.CompanyVehicle_RegistrationPlate,
                                d.CompanyVehicle_Title,
                              ]
                                .filter(Boolean)
                                .join(" - ") ||
                                `Vehicle #${d.CompanyVehicle_Id}`}
                            </Link>
                          ) : (
                            <span className="text-muted">Not assigned</span>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-soft-primary btn-sm me-1"
                            onClick={() => openAssign(d)}
                            disabled={!d.ProviderDeviceId}
                          >
                            {d.CompanyVehicle_Id
                              ? "Change vehicle"
                              : "Assign to vehicle"}
                          </button>
                          <a
                            className="btn btn-soft-secondary btn-sm"
                            href={gpsLiveDeviceUrl(d)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open this device in GPSLive"
                          >
                            GPSLive <i className="ri-external-link-line"></i>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          <AppModal
            open={!!assignDevice}
            onClose={() => setAssignDevice(null)}
            title="Assign device to vehicle"
            headerAccent="indigo"
            size="sm"
            loading={assignSaving}
            loadingText="Saving..."
            onSubmit={handleAssignSave}
            submitLabel="Assign"
            submitColor="success"
          >
            <p className="mb-2">
              <strong>{assignDevice?.Name || "Unnamed device"}</strong>
              {assignDevice?.ProviderDeviceId
                ? ` (#${assignDevice.ProviderDeviceId})`
                : ""}
            </p>
            {assignDevice?.CompanyVehicle_Id && (
              <p className="text-muted small">
                Currently on{" "}
                {[
                  assignDevice.CompanyVehicle_RegistrationPlate,
                  assignDevice.CompanyVehicle_Title,
                ]
                  .filter(Boolean)
                  .join(" - ") || `Vehicle #${assignDevice.CompanyVehicle_Id}`}
                . Choosing another vehicle moves it there.
              </p>
            )}
            <div className="d-flex justify-content-between align-items-center">
              <Label className="mb-1">Vehicle</Label>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mb-1"
                onClick={() => setCreateVehicleReg("")}
              >
                <i className="ri-add-line"></i> New vehicle
              </button>
            </div>
            <CreatableSelect
              classNamePrefix="select2-selection"
              isLoading={vehicles === null}
              options={vehicleOptions}
              value={
                vehicleOptions.find((o) => o.value === assignVehicleId) || null
              }
              onChange={(o) => setAssignVehicleId(o?.value || null)}
              onCreateOption={(text) => setCreateVehicleReg(text.trim())}
              formatCreateLabel={(text) => `Create vehicle "${text}"`}
              placeholder="Search or create a vehicle..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
            />
          </AppModal>

          {createVehicleReg !== null && (
            <CreateEditCompanyVehicle
              modal={createVehicleReg !== null}
              toggle={() => setCreateVehicleReg(null)}
              isEdit={false}
              initialRegistration={createVehicleReg}
              onCreated={handleVehicleCreated}
            />
          )}
        </>
      )}
    </Card>
  );
}
